import { Box, Flex, Link } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';

import { useBlockFollowup } from '../../../hooks';
import { Card, StatusPill } from '../primitives';
import type { InterviewCardData, InterviewListBlockData } from '../../../types';
import { SuggestedActionChips } from '../SuggestedActionChips';

interface InterviewListBlockProps {
  data: InterviewListBlockData;
}

// Meeting status → badge colors + product-style labels ("Interview
// completed"). The backend may suffix " · scorecard pending" — match on the
// leading word, keep the suffix visible.
const STATUS_STYLE: Record<
  string,
  { bg: string; color: string; label: string }
> = {
  completed: {
    bg: 'success-50',
    color: 'success-700',
    label: 'Interview completed',
  },
  scheduled: {
    bg: 'primary-50',
    color: 'primary-600',
    label: 'Interview scheduled',
  },
  in_progress: { bg: 'primary-50', color: 'primary-600', label: 'In progress' },
  cancelled: {
    bg: 'secondary-100',
    color: 'secondary-600',
    label: 'Cancelled',
  },
  no_show: { bg: 'warning-50', color: 'warning-700', label: 'No show' },
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return null;
  const key = status.toLowerCase().split(' ')[0].replace(/·$/, '');
  const s = STATUS_STYLE[key] ?? {
    bg: 'secondary-100',
    color: 'secondary-600',
    label: status.replace(/_/g, ' '),
  };
  return (
    <Flex align="center" gap="6px" flexShrink={0}>
      <StatusPill label={s.label} color={`var(--chakra-colors-${s.color})`} />
      {status.includes('scorecard pending') && (
        <StatusPill label="Scorecard pending" />
      )}
    </Flex>
  );
};

// "21:00 - 21:30, 20/02/2026" in the VIEWER'S timezone — epochs come from the
// backend precisely so this renders local, like the product's interview card.
// Deterministic for a fixed input (no reads of the current time).
const timeRange = (iv: InterviewCardData): string | undefined => {
  if (!iv.startTime) return iv.when || undefined;
  const start = new Date(iv.startTime * 1000);
  const hm = (d: Date) =>
    d.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  const date = start.toLocaleDateString([], {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  if (!iv.endTime) return `${hm(start)}, ${date}`;
  return `${hm(start)} - ${hm(new Date(iv.endTime * 1000))}, ${date}`;
};

const durationLabel = (iv: InterviewCardData): string | undefined => {
  if (!iv.startTime || !iv.endTime || iv.endTime <= iv.startTime)
    return undefined;
  const mins = Math.round((iv.endTime - iv.startTime) / 60);
  return mins >= 60 ? `${Math.round(mins / 6) / 10}h` : `${mins}m`;
};

const locationLabel = (location?: string): string | undefined => {
  if (!location) return undefined;
  const l = location.toLowerCase();
  if (l.includes('phone')) return 'Phone Interview';
  if (l.includes('video') || l.includes('virtual')) return 'Video Interview';
  if (l.includes('person') || l.includes('onsite')) return 'In-person';
  return location.replace(/_/g, ' ');
};

// The meta line mirrors the product card: job · time range · duration · kind.
const metaLine = (iv: InterviewCardData): string =>
  [iv.job, timeRange(iv), durationLabel(iv), locationLabel(iv.location)]
    .filter(Boolean)
    .join(' · ');

// Participants: candidate first (indexer convention), interviewers after.
const participantsLine = (iv: InterviewCardData): string | undefined => {
  const names = iv.participants ?? [];
  const candidate = iv.candidate || names[0];
  if (!candidate) return undefined;
  const interviewers = names.filter((n) => n !== candidate);
  const parts = [`${candidate} (Candidate)`];
  if (interviewers.length > 0) {
    parts.push(`Interviewers: ${interviewers.slice(0, 3).join(', ')}`);
  }
  return parts.join(' · ');
};

const InterviewCard = ({ interview }: { interview: InterviewCardData }) => {
  const meta = metaLine(interview);
  const people = participantsLine(interview);
  return (
    <Card data-test-id={`interview-card-${interview.id}`}>
      <Flex align="center" justify="space-between" gap="10px">
        <Text variant="span" className="text-secondary-900 font-semibold">
          {interview.title}
        </Text>
        <StatusBadge status={interview.status} />
      </Flex>
      {meta && (
        <Text
          variant="small"
          className="text-secondary-500"
          style={{ marginTop: 2, display: 'block' }}
        >
          {meta}
        </Text>
      )}
      {people && (
        <Text
          variant="small"
          className="text-secondary-400"
          style={{ marginTop: 2, display: 'block' }}
        >
          {people}
        </Text>
      )}
      {interview.url && (
        <Box mt="8px">
          <Link
            href={interview.url}
            isExternal
            color="primary-500"
            onClick={(e) => e.stopPropagation()}
            display="inline-block"
          >
            <Text variant="small" className="font-medium text-primary-500">
              View interview ↗
            </Text>
          </Link>
        </Box>
      )}
    </Card>
  );
};

// A scannable list of interview cards — search results for "my past
// interviews", "interviews this week", etc. Follow-up chips below, primary
// first. Not selectable (v1): interviews have no bulk actions.
export const InterviewListBlock = ({ data }: InterviewListBlockProps) => {
  const interviews = data.interviews ?? [];
  const allIds = data.ids ?? interviews.map((iv) => iv.id);
  const { sendFollowup, isSubmitting } = useBlockFollowup({
    toolCallId: data.toolCallId,
    allIds,
    selectedIds: [],
  });
  if (interviews.length === 0) return null;

  return (
    <Flex
      direction="column"
      gap="8px"
      w="100%"
      minW="0"
      data-test-id="agentforge-interview-list"
    >
      {data.summary && (
        <Text variant="small" className="text-secondary-500">
          {data.summary}
        </Text>
      )}
      {data.interviews.map((interview) => (
        <InterviewCard key={interview.id} interview={interview} />
      ))}
      {data.suggestedActions && data.suggestedActions.length > 0 && (
        <Box mt="4px">
          <SuggestedActionChips
            actions={data.suggestedActions}
            onSelect={sendFollowup}
            disabled={isSubmitting}
            testIdPrefix="interviewlist-suggested-action"
          />
        </Box>
      )}
    </Flex>
  );
};
